import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, useDragControls, type PanInfo } from 'framer-motion';
import { addDays, format, isSameDay, startOfDay } from 'date-fns';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { CalendarEventDto } from '@cc/types';

interface Props {
  weekStart: Date;
  events: CalendarEventDto[];
  accountColors: Record<string, string>;
  onEventClick: (e: CalendarEventDto) => void;
  onSlotClick: (d: Date) => void;
}

// Hour band shown in the scroll viewport. Earlier / later events are still
// visible — scroll to them; the band just sets the initial viewport.
const VISIBLE_HOUR_START = 7;
const VISIBLE_HOUR_END = 21;
const HOUR_PX = 60; // 1 hour = 60 px → 15-min snap = 15 px
const TOTAL_HOURS = 24;
const TOTAL_HEIGHT_PX = HOUR_PX * TOTAL_HOURS;
const SNAP_MIN = 15;

/**
 * Week grid. 7 day-columns (Sun -> Sat), hourly rows. Events render as
 * absolutely-positioned cards within their day column. Drag any event:
 *  - vertically to change time within the same day (snaps to 15-min)
 *  - horizontally to change the day (full-day-width per column)
 * On drop, calls PATCH /api/calendar/events/:id with the new ISO times.
 * Click an empty slot to open quick-add prefilled with that time + day.
 */
export function WeekView({
  weekStart,
  events,
  accountColors,
  onEventClick,
  onSlotClick,
}: Props) {
  const queryClient = useQueryClient();
  const gridRef = useRef<HTMLDivElement>(null);
  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  const updateMutation = useMutation({
    mutationFn: (args: { id: string; startIso: string; endIso: string; verb: 'move' | 'resize' }) =>
      api.updateCalendarEvent(args.id, {
        startIso: args.startIso,
        endIso: args.endIso,
        notifyAttendees: false,
      }),
    onSuccess: (_d, args) => {
      toast.success(args.verb === 'resize' ? 'Resized.' : 'Moved.');
      void queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
    onError: (_e, args) =>
      toast.error(args.verb === 'resize' ? 'Could not resize event.' : 'Could not move event.'),
  });

  // All-day band at top: one row per day with chips
  const allDayByDay = useMemo(() => {
    const map = new Map<string, CalendarEventDto[]>();
    for (const e of events) {
      if (!e.isAllDay) continue;
      const key = format(new Date(e.startTime), 'yyyy-MM-dd');
      const list = map.get(key) ?? [];
      list.push(e);
      map.set(key, list);
    }
    return map;
  }, [events]);

  // Timed events grouped by day for column rendering
  const timedByDay = useMemo(() => {
    const map = new Map<string, CalendarEventDto[]>();
    for (const e of events) {
      if (e.isAllDay) continue;
      const key = format(new Date(e.startTime), 'yyyy-MM-dd');
      const list = map.get(key) ?? [];
      list.push(e);
      map.set(key, list);
    }
    return map;
  }, [events]);

  return (
    <div className="rounded-md border border-border bg-card">
      <div className="grid grid-cols-[3rem_repeat(7,1fr)] border-b border-border">
        <div />
        {days.map((d) => (
          <div
            key={d.toISOString()}
            className={cn(
              'px-2 py-2 text-center text-xs',
              isSameDay(d, new Date()) && 'bg-accent/30',
            )}
          >
            <div className="font-medium">{format(d, 'EEE')}</div>
            <div className="text-muted-foreground">{format(d, 'd MMM')}</div>
          </div>
        ))}
      </div>

      {/* All-day band */}
      <div className="grid grid-cols-[3rem_repeat(7,1fr)] border-b border-border bg-muted/20">
        <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">
          all day
        </div>
        {days.map((d) => {
          const key = format(d, 'yyyy-MM-dd');
          const list = allDayByDay.get(key) ?? [];
          return (
            <div key={key} className="min-h-[28px] border-l border-border px-1 py-1">
              {list.map((e) => (
                <button
                  key={e.id}
                  onClick={() => onEventClick(e)}
                  className="mb-0.5 block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px]"
                  style={{
                    backgroundColor: `${accountColors[e.accountId] ?? '#888'}33`,
                    borderLeft: `2px solid ${accountColors[e.accountId] ?? '#888'}`,
                  }}
                >
                  {e.summary || '(no title)'}
                </button>
              ))}
            </div>
          );
        })}
      </div>

      {/* Hourly grid */}
      <div
        className="relative overflow-y-auto"
        style={{ height: HOUR_PX * (VISIBLE_HOUR_END - VISIBLE_HOUR_START) + 12 }}
        ref={(el) => {
          if (el && el.scrollTop === 0) {
            // Initial scroll to the visible band.
            el.scrollTop = VISIBLE_HOUR_START * HOUR_PX;
          }
        }}
      >
        <div
          ref={gridRef}
          className="relative grid grid-cols-[3rem_repeat(7,1fr)]"
          style={{ height: TOTAL_HEIGHT_PX }}
        >
          {/* Hour labels column */}
          <div className="relative">
            {Array.from({ length: TOTAL_HOURS }, (_, h) => (
              <div
                key={h}
                className="absolute right-1 text-[10px] text-muted-foreground"
                style={{ top: h * HOUR_PX + 2 }}
              >
                {h === 0 ? '' : `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? 'a' : 'p'}`}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((d, colIdx) => {
            const key = format(d, 'yyyy-MM-dd');
            const list = timedByDay.get(key) ?? [];
            return (
              <DayColumn
                key={key}
                day={d}
                colIndex={colIdx}
                events={list}
                accountColors={accountColors}
                onEventClick={onEventClick}
                onSlotClick={onSlotClick}
                onDragCommit={(eventId, deltaMinutes, deltaDays) => {
                  const e = events.find((x) => x.id === eventId);
                  if (!e) return;
                  const newStart = new Date(
                    new Date(e.startTime).getTime() + deltaMinutes * 60_000 + deltaDays * 24 * 60 * 60_000,
                  );
                  const newEnd = new Date(
                    new Date(e.endTime).getTime() + deltaMinutes * 60_000 + deltaDays * 24 * 60 * 60_000,
                  );
                  updateMutation.mutate({
                    id: eventId,
                    startIso: newStart.toISOString(),
                    endIso: newEnd.toISOString(),
                    verb: 'move',
                  });
                }}
                onResizeCommit={(eventId, deltaMinutes) => {
                  const e = events.find((x) => x.id === eventId);
                  if (!e) return;
                  // Resize moves end only; clamp so it stays ≥ 15 min after start.
                  const start = new Date(e.startTime);
                  const newEnd = new Date(new Date(e.endTime).getTime() + deltaMinutes * 60_000);
                  const minEnd = new Date(start.getTime() + 15 * 60_000);
                  const clamped = newEnd < minEnd ? minEnd : newEnd;
                  if (clamped.getTime() === new Date(e.endTime).getTime()) return;
                  updateMutation.mutate({
                    id: eventId,
                    startIso: start.toISOString(),
                    endIso: clamped.toISOString(),
                    verb: 'resize',
                  });
                }}
              />
            );
          })}

          {/* Now-line marker */}
          <NowLine />
        </div>
      </div>
    </div>
  );
}

function DayColumn({
  day,
  events,
  accountColors,
  onEventClick,
  onSlotClick,
  onDragCommit,
  onResizeCommit,
}: {
  day: Date;
  colIndex: number;
  events: CalendarEventDto[];
  accountColors: Record<string, string>;
  onEventClick: (e: CalendarEventDto) => void;
  onSlotClick: (d: Date) => void;
  onDragCommit: (eventId: string, deltaMinutes: number, deltaDays: number) => void;
  onResizeCommit: (eventId: string, deltaMinutes: number) => void;
}) {
  const isToday = isSameDay(day, new Date());
  // Store the day key so the EventCard's drag-end can look it up by drop
  // target rather than pixel-math (which fell apart when the user dragged
  // off the grid and we computed a nonsense day delta).
  const dayKey = format(day, 'yyyy-MM-dd');
  return (
    <div
      data-week-day-col
      data-week-day-key={dayKey}
      className={cn(
        'relative border-l border-border',
        isToday && 'bg-accent/10',
      )}
      onClick={(ev) => {
        // Only count clicks on the empty area (target IS the column), not events.
        if (ev.target !== ev.currentTarget) return;
        const rect = (ev.currentTarget as HTMLDivElement).getBoundingClientRect();
        const yWithinCol = ev.clientY - rect.top;
        const minutes = Math.round((yWithinCol / HOUR_PX) * 60 / SNAP_MIN) * SNAP_MIN;
        const slot = startOfDay(day);
        slot.setMinutes(minutes);
        onSlotClick(slot);
      }}
    >
      {/* hourly subgrid lines */}
      {Array.from({ length: TOTAL_HOURS }, (_, h) => (
        <div
          key={h}
          className="pointer-events-none absolute left-0 right-0 border-t border-border/30"
          style={{ top: h * HOUR_PX }}
        />
      ))}
      {events.map((e) => (
        <EventCard
          key={e.id}
          event={e}
          sourceDayKey={dayKey}
          color={accountColors[e.accountId] ?? '#888'}
          onClick={() => onEventClick(e)}
          onDragCommit={onDragCommit}
          onResizeCommit={onResizeCommit}
        />
      ))}
    </div>
  );
}

function EventCard({
  event,
  sourceDayKey,
  color,
  onClick,
  onDragCommit,
  onResizeCommit,
}: {
  event: CalendarEventDto;
  sourceDayKey: string;
  color: string;
  onClick: () => void;
  onDragCommit: (eventId: string, deltaMinutes: number, deltaDays: number) => void;
  onResizeCommit: (eventId: string, deltaMinutes: number) => void;
}) {
  const start = new Date(event.startTime);
  const end = new Date(event.endTime);
  const dayStart = startOfDay(start);
  const minutesFromMidnight = (start.getTime() - dayStart.getTime()) / 60_000;
  const durationMin = Math.max(15, (end.getTime() - start.getTime()) / 60_000);
  const top = (minutesFromMidnight / 60) * HOUR_PX;
  const baseHeight = (durationMin / 60) * HOUR_PX;

  const [dragging, setDragging] = useState(false);
  // Pixel offset applied to the bottom edge while resizing. Snapped to the
  // 15-min grid for visual feedback (the commit also snaps for the API call).
  const [resizeDeltaPx, setResizeDeltaPx] = useState<number | null>(null);
  // Manual drag control — framer only starts a drag when the card body
  // initiates it. The bottom-edge resize handle is a sibling that bypasses
  // this entirely, so grabbing the edge never accidentally triggers a move.
  const dragControls = useDragControls();

  function handleDragEnd(_event: unknown, info: PanInfo) {
    setDragging(false);
    // Resolve drop target by walking the elements under the release point and
    // finding the one with data-week-day-key. Pixel-math via offsetWidth used
    // to fall apart when the user dragged off the grid (no day-col under the
    // cursor → fallback width=1 → deltaDays ballooned and the event vanished
    // weeks into the future). Looking up the key directly is bullet-proof.
    if (!info.point) return;
    const els = document.elementsFromPoint(info.point.x, info.point.y);
    const dropCol = els
      .map((el) => (el as HTMLElement).dataset?.weekDayKey)
      .find((k): k is string => typeof k === 'string' && k.length > 0);
    if (!dropCol) return; // released outside the grid — no-op

    // deltaDays from key-diff. Both keys are YYYY-MM-DD; parse to local dates
    // and subtract to avoid DST weirdness from raw ms arithmetic.
    const deltaDays = ymdDayDiff(sourceDayKey, dropCol);

    // deltaMinutes from vertical offset, snapped to 15 min.
    const deltaMinutes = Math.round(info.offset.y / HOUR_PX * 60 / SNAP_MIN) * SNAP_MIN;

    if (deltaMinutes === 0 && deltaDays === 0) return;
    onDragCommit(event.id, deltaMinutes, deltaDays);
  }

  function handleResizePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    // The bottom-edge handle initiates resize. We need to stop the native
    // pointerdown from bubbling to the parent motion.div, otherwise framer's
    // drag listener (attached directly to the parent DOM node) would also
    // fire and treat this gesture as a move. React's `e.stopPropagation` is
    // synthetic-only; the native event must be stopped explicitly.
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    e.preventDefault();
    const target = e.currentTarget;
    const pointerId = e.pointerId;
    const startY = e.clientY;
    target.setPointerCapture(pointerId);

    function snapPx(deltaY: number): number {
      // Snap to multiples of (SNAP_MIN / 60) * HOUR_PX so visual feedback
      // matches the eventual committed value.
      const snapPxUnit = (SNAP_MIN / 60) * HOUR_PX;
      return Math.round(deltaY / snapPxUnit) * snapPxUnit;
    }

    function handleMove(ev: PointerEvent) {
      const raw = ev.clientY - startY;
      const snapped = snapPx(raw);
      // Floor the visual height at 15 min from start.
      const minDelta = -(baseHeight - (SNAP_MIN / 60) * HOUR_PX);
      setResizeDeltaPx(Math.max(snapped, minDelta));
    }

    function handleUp(ev: PointerEvent) {
      target.releasePointerCapture(pointerId);
      target.removeEventListener('pointermove', handleMove);
      target.removeEventListener('pointerup', handleUp);
      target.removeEventListener('pointercancel', handleUp);
      const raw = ev.clientY - startY;
      const snappedPx = snapPx(raw);
      setResizeDeltaPx(null);
      const deltaMinutes = Math.round(snappedPx / HOUR_PX * 60 / SNAP_MIN) * SNAP_MIN;
      if (deltaMinutes === 0) return;
      onResizeCommit(event.id, deltaMinutes);
    }

    target.addEventListener('pointermove', handleMove);
    target.addEventListener('pointerup', handleUp);
    target.addEventListener('pointercancel', handleUp);
  }

  const renderedHeight = resizeDeltaPx == null
    ? Math.max(baseHeight, 20)
    : Math.max(baseHeight + resizeDeltaPx, (SNAP_MIN / 60) * HOUR_PX);
  const resizing = resizeDeltaPx != null;

  return (
    <motion.div
      drag
      dragListener={false}
      dragControls={dragControls}
      dragMomentum={false}
      dragElastic={0}
      onDragStart={() => setDragging(true)}
      onDragEnd={handleDragEnd}
      whileDrag={{ scale: 1.02, zIndex: 30 }}
      className={cn(
        'absolute left-1 right-1 overflow-hidden rounded text-[11px] shadow-sm',
        resizing && 'ring-1 ring-primary/60',
      )}
      style={{
        top,
        height: renderedHeight,
        backgroundColor: `${color}33`,
        borderLeft: `2px solid ${color}`,
      }}
    >
      {/* Card body — initiates the move-drag via dragControls.start. The
         resize handle below is a sibling that does NOT call start, so the
         two gestures are cleanly separated. */}
      <div
        onPointerDown={(e) => {
          // Don't initiate a drag if the user clicked the resize handle
          // (event.target check covers nested elements).
          if ((e.target as HTMLElement).dataset?.resizeHandle === 'true') return;
          dragControls.start(e);
        }}
        onClick={() => {
          if (dragging || resizing) return;
          onClick();
        }}
        className={cn('h-full w-full cursor-grab px-1.5 py-1', dragging && 'cursor-grabbing')}
      >
        <div className="truncate font-medium leading-tight">
          {event.summary || '(no title)'}
        </div>
        <div className="truncate text-[10px] opacity-70">
          {format(start, 'h:mm a')} –{' '}
          {format(
            resizeDeltaPx != null
              ? new Date(end.getTime() + (resizeDeltaPx / HOUR_PX) * 60 * 60_000)
              : end,
            'h:mm a',
          )}
        </div>
      </div>
      {/* South-edge resize handle. Stops at the handle — never reaches the
         dragControls.start above, so resize never fires a move. */}
      <div
        data-resize-handle="true"
        onPointerDown={handleResizePointerDown}
        className="absolute inset-x-0 bottom-0 h-1.5 cursor-ns-resize"
        style={{ touchAction: 'none' }}
        aria-hidden
      />
    </motion.div>
  );
}

/** Diff two YYYY-MM-DD keys in whole days (b − a). DST-safe via local-date construction. */
function ymdDayDiff(a: string, b: string): number {
  const ap = a.split('-');
  const bp = b.split('-');
  const aDate = new Date(Number(ap[0]), Number(ap[1]) - 1, Number(ap[2]));
  const bDate = new Date(Number(bp[0]), Number(bp[1]) - 1, Number(bp[2]));
  return Math.round((bDate.getTime() - aDate.getTime()) / (24 * 60 * 60 * 1000));
}

function NowLine() {
  const now = new Date();
  const top = (now.getHours() * 60 + now.getMinutes()) / 60 * HOUR_PX;
  return (
    <div
      className="pointer-events-none absolute left-12 right-0 z-20 border-t border-red-500"
      style={{ top }}
    >
      <div className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-red-500" />
    </div>
  );
}
